// React
import * as React from "react";
import _ from 'lodash';
import moment from 'moment';
import Tippy from '@tippyjs/react'
import { Property } from "csstype";

// Material UI components
import Checkbox from 'material-ui/Checkbox';
import * as TableMUI from 'material-ui/Table';
import TextField from 'material-ui/TextField';
import FlatButton from 'material-ui/FlatButton';
import IconButton from 'material-ui/IconButton';

// Icons
import AddIcon from 'material-ui/svg-icons/content/add';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import RadioOn from 'material-ui/svg-icons/toggle/radio-button-checked';
import RadioOff from 'material-ui/svg-icons/toggle/radio-button-unchecked';

// Internal components
import { defaultWidgetContainerPadding, IconURIPath, PartialIWidgetThingsProps, WidgetLocalizationKeys } from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { TimeFormat, DateFormat } from "@Utils/Formats";
import { Currency, SelectAllMode } from "@Utils/Formats.UI";
import { isStringEmpty, isDefined, getRandomSmallKey, IsNullOrEmpty, getRandomKey } from "@Utils/Micro";
import { DomUtils } from "@Utils/Dom";
import { DebugEx } from "@Utils/DebugEx";

import { ExcelOptions } from '../Charts/TimeSeriesStructs';

import {
    ReactTable,
    ReactTableColumn,
    ColumnType,
    IconType,
    ToolbarButton,
    PivotSettings,
    PivotValueSelectionType,
    AddDuplicateRowPolicy,
    ReactTableStatics,
    LocalizationLabelKey as LocLabelKeyBase,
    ISelectionActionIcon,
    IRequestServerData,
    PageChange,
    DropDownItem,
    ReactTableTextAlign
} from '../../../Tables/ReactTable';
import { EnumType } from '@Primitive/FieldEditors';
import { ToolbarIconInfo, ToolbarIconInfo_GetSettingList } from '@Primitive/ToolbarIconButton';
import { eIconType, SettingsDialogConf, SettingsElementAll, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { IWidgetDataValidateResp } from "@UI/Widgets/IWidgetDataValidateResp";
import { WebNode } from '@API/WebNode';
import { PortData, ReduxThingState, StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { StateStorage } from '@Utils/StateStorage';
import { SettingsList } from '@Primitive/SettingsList';
import { StateManager } from "@Utils/StateManager";
import { CSVImportSettings } from "@UI/Tables/CSVImport";
// ------------------------------------------------------------------------------------------------
const AutoHiddableWidth = 500;
const __COLUMNS_VISIBILITY_KEY__ = '__COLUMNS_VISIBILITY__';

export interface TableSettings {
    Key: string;
    Columns: ColumnSettings[];
    DynamicColumnsWidth: boolean;
    PageListSettings: { PageList: number[], DefaultSelected: number };
    PriceCurrency: Currency;
    DateFormat: DateFormat;
    Editable: boolean;
    Selectable: boolean;
    SelectAllTooltip?: string;
    SelectAllMode?: number;
    SelectableTypeRadio: boolean;
    AutoSend: boolean;
    filterable: boolean;
    timeFormat: TimeFormat;
    WrapEnableByDefault: boolean;
    FilterEnableByDefault: boolean;
    ShowSubComponent: boolean;
    OnlyHiddenColumnsInSubcomponent: boolean;
    ShowEditRow: boolean;
    OnSaveSendEverything: boolean;
    PivotSupport: boolean;
    PivotSettings: PivotSettings;
    EnumSettings: IEnumSettings[];
    DropdownMenuSettings: any[]; //todo
    DownloadExcel: boolean;
    DownloadExcelOptions: ExcelOptions;
    CSVImport: boolean;
    CSVImportSettings: CSVImportSettings;
    DefaultImageURL: string;
    ImgWidth: number;
    ImgHeight: number;
    ObjectFit: Property.ObjectFit;
    ShowWrapText: boolean;
    ShowColVis: boolean;
    PagingSupport: boolean;
    ShowAddRow: boolean;
    AddDuplicateRowPolicy: AddDuplicateRowPolicy;
    ToolbarIcons: ToolbarIconInfo[];
    ToolbarHeight: number;
    ToolbarIconSize: number;
    SendSelectedButton: ToolbarIconInfo;
    Theme: {
        toolbarIconColor?: string;

        rowFontColor?: string;
        rowLineBottomColor?: string;
        rowLineBottomStyle?: string;
        rowLineBottomWidth?: number;
        rowSelectedBackgroundColor?: string;
        rowEditedBackgroundColor?: string;
        rowAddedBackgroundColor?: string;
        textAlign?: ReactTableTextAlign;

        headFontColor?: string;
        headLineBottomColor?: string;
        headLineBottomStyle?: string;
        headLineBottomWidth?: number;

        selectFontColor?: string;
        selectBackgroundColor?: string;
        selectBackgroundColorFocused?: string;
        selectBackgroundColorSelected?: string;
    },
    ServerSideProcessing: boolean;
}

interface IEnumSettings {
    /**
     * Corresponding to Column Key
     */
    key: string;
    data: EnumType[];
    multi: boolean;
    creatable: boolean;
    useDialog: boolean;
}

interface IDropdownMenuSettings {
    /**
     * Corresponding to column key
     */
    key: string;
    column: ColumnSettings;
    dropdownMenuItems: ToolbarIconInfo[];
}

const getDefaultPageListSettings = (): { PageList: number[], DefaultSelected: number } => {
    return { PageList: [25, 50, 100, 200], DefaultSelected: 50 };
};

export interface ColumnSettings {
    Key: string;
    JsonName: string;
    JsonNameUniqueError: boolean;
    Title: string;
    Type: ColumnType;
    IsPrimaryKey: boolean;
    Sortable: boolean;
    Filtering: boolean;
    Hidden: boolean;
    AutoHiddable: boolean;
    Tooltip: string;
    Icon: IconType;
    Width: number;
    PivotBy: boolean;
    PivotSettings?: PivotSettings;

    // For boolean type
    CheckedIcon: string;
    UncheckedIcon: string;

    // Highlight
    BackgroundColorAccessor: string;
    FontColorAccessor?: string;
    StyleAccessor: string;
    HeaderBackgroundColor?: string;

    // Edit
    NonEditable: boolean;
    VisibleInAdd: boolean;

    /**
     * Used only for custom columns creation.
     */
    EnumSettings?: IEnumSettings;
    DropdownSettings?: IDropdownMenuSettings;

    /**
     * Used for defining whether a column is mandatory
     */
    IsMandatory?: boolean;

    // Settings form progress
    progressIsCircle?: boolean;
    progressColor?: string;
    progressWidth?: number;
    progressShowValue?: boolean;
    progressTrailColor?: string;
    progressTrailWidth?: number;
}

export const TableCellCommonStyle: React.CSSProperties = {
    textAlign: 'center',
    paddingLeft: 8,
    paddingRight: 8
}
// ------------------------------------------------------------------------------------------------------
const enum TableKeys {
    EditDataPortKey = 'Edit_Data',
    SelectDataPortKey = 'Select_Data',
    InsertOrUpdatePortKey = 'Insert_or_Update',
    Settings = 'Settings',
    CustomColumns = 'Custom_Columns',
    DataPortKey = 'data_port',
    AddDataPortKey = 'Add_Data',
    onCellClick = 'onCellClick',
    onToolbarIconClick = 'onToolbarIconClick',
    onRowUpdate = 'onRowUpdate',
    onPageChange = 'onPageChange',
    onDropdownMenuItemClick = 'onDropdownMenuItemClick',
    ServerData = 'server_data',
    FetchData = 'fetch_data',
    onVisibilityChange = 'onVisibilityChange'
}

export enum onCellClickSrcKeys {
    Cell = 'cell',
    SubComponent = 'subComponent'
}
// ------------------------------------------------------------------------------------------------------
const DefaultTableSettings: TableSettings = {
    Key: TableKeys.DataPortKey,
    Columns: [],
    DynamicColumnsWidth: true,
    PriceCurrency: Currency.USD,
    DateFormat: DateFormat.Short,
    PageListSettings: getDefaultPageListSettings(),
    Editable: false,
    Selectable: false,
    SelectAllTooltip: null,
    SelectAllMode: SelectAllMode.All,
    SelectableTypeRadio: false,
    AutoSend: false,
    filterable: true,
    timeFormat: TimeFormat.short,
    WrapEnableByDefault: true,
    FilterEnableByDefault: false,
    ShowSubComponent: false,
    OnlyHiddenColumnsInSubcomponent: false,
    ShowEditRow: false,
    OnSaveSendEverything: false,
    PivotSupport: false,
    PivotSettings: {
        Type: PivotValueSelectionType.Avg,
    },
    EnumSettings: [],
    DropdownMenuSettings: [],
    DownloadExcel: false,
    DownloadExcelOptions: {
        fileName: "Data"
    },
    CSVImport: false,
    CSVImportSettings: {
        FirstRowIsHeaders: false,
        TrimColumnData: false,
        DynamicTyping: true
    },
    DefaultImageURL: "",
    ImgWidth: 24,
    ImgHeight: 24,
    ObjectFit: "cover",
    ShowWrapText: true,
    ShowColVis: true,
    PagingSupport: true,
    ShowAddRow: false,
    AddDuplicateRowPolicy: AddDuplicateRowPolicy.Allow,
    ToolbarIcons: [],
    ToolbarHeight: 25,
    ToolbarIconSize: 18,
    SendSelectedButton: {
        icon: 'fas fa-paper-plane',
        tooltip: ReactTableStatics.GetLocalizationDictionary()[LocLabelKeyBase.sendSelected]
    },
    Theme: {
        headFontColor: "#575757",
        headLineBottomColor: "rgb(87, 87, 87)",
        headLineBottomStyle: "hidden",
        headLineBottomWidth: 1,
        rowAddedBackgroundColor: "#0080005e",
        rowEditedBackgroundColor: "#ff000038",
        rowSelectedBackgroundColor: "lightgreen",
        rowFontColor: "#575757",
        rowLineBottomColor: "rgb(87, 87, 87)",
        rowLineBottomStyle: "hidden",
        rowLineBottomWidth: 1,
        toolbarIconColor: "#575757",
        selectFontColor: "#575757",
        selectBackgroundColor: "#ffffff",
        selectBackgroundColorFocused: "#deebff",
        selectBackgroundColorSelected: "#2684ff",
        textAlign: ReactTableTextAlign.Center
    },
    ServerSideProcessing: false
};
// ------------------------------------------------------------------------------------------------------
const DefaultColumnSettings: ColumnSettings = {
    Key: "",
    JsonName: "",
    JsonNameUniqueError: false,
    Title: "",
    Filtering: true,
    IsPrimaryKey: false,
    Sortable: true,
    Type: ColumnType.text,
    Icon: IconType.info,
    AutoHiddable: false,
    Tooltip: "",
    Width: 10,
    Hidden: false,
    NonEditable: false,
    IsMandatory: false,
    PivotBy: false,
    CheckedIcon: "fa-check-square-o",
    UncheckedIcon: "fa-square-o",
    BackgroundColorAccessor: null,
    FontColorAccessor: null,
    StyleAccessor: null,
    VisibleInAdd: true,
    progressIsCircle: false,
    progressColor: "#3498ff",
    progressWidth: 6,
    progressShowValue: true,
    progressTrailColor: "#e5e5ea",
    progressTrailWidth: 6,
};
// ------------------------------------------------------------------------------------------------------
export interface TableData extends TableSettings {
    // old settings that we are going to delete in the future ...
    TableSettings?: TableSettings;
}
// ------------------------------------------------------------------------------------------------------
export interface TableState {
    // data version to force data redrawing when
    // we have new data from cyan but avoid the redraw in edit mode.
    dataVersion: number;

    columns: ReactTableColumn[];
    enumSettings: IEnumSettings[];
    dropdownMenuSettings: IDropdownMenuSettings[];

    // Overriden widget configuration.
    settings: Partial<TableSettings>;

    wrapTextEnabled: boolean;
    filterEnabled: boolean;

    // for server-side processing
    loading: boolean;
}
// ------------------------------------------------------------------------------------------------------
interface StorageData {
    wrapTextEnabled: boolean;
    filterEnabled: boolean;
}
// ------------------------------------------------------------------------------------------------------
interface IOnRowUpdate {
    index: number;
    values: any;
}
// ------------------------------------------------------------------------------------------------------
interface IServerData {
    rows: any[];
    pages: number;
    reset: boolean;
}
// ------------------------------------------------------------------------------------------------------
export class Table extends React.Component<PartialIWidgetThingsProps<TableData>, TableState> {
    // ---------------------------------------------------------------------------------------------------
    private _stateStorage: StateStorage;
    private _tableData: any[] = [];
    private _colvis: { [key: string]: boolean } = null;
    private _pages = null;
    private _reset = null;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: PartialIWidgetThingsProps<TableData>) {
        super(props);

        this._stateStorage = StateStorage.GetInstance();
        const widgetData = props.WidgetData;

        let storageData: StorageData = this._stateStorage.GetState(this.props.ThingKey);
        if (storageData == null)
            storageData = {
                wrapTextEnabled: widgetData.WrapEnableByDefault,
                filterEnabled: widgetData.FilterEnableByDefault
            };

        this.state = {
            dataVersion: 0,
            columns: [],
            enumSettings: _.cloneDeep(this.props.WidgetData.EnumSettings),
            dropdownMenuSettings: _.cloneDeep(this.props.WidgetData.DropdownMenuSettings),
            settings: {
                // For now, use only these two.
                Editable: widgetData.Editable,
                ShowAddRow: widgetData.ShowAddRow,
                Selectable: widgetData.Selectable,
                SelectAllTooltip: widgetData.SelectAllTooltip,
                SelectAllMode: widgetData.SelectAllMode,
                SelectableTypeRadio: widgetData.SelectableTypeRadio
            },
            wrapTextEnabled: storageData.wrapTextEnabled,
            filterEnabled: storageData.filterEnabled,
            loading: false
        };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        this.updateColumns();
        this._updateUsingTimestamp(this.props);

        // Link update port
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.RegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.UnRegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------
    stateUpdate = (state: ReduxThingState, forceUpdate: boolean, changedSubKeys: string[]) => {
        for (const portKey of changedSubKeys) {
            let port = state.PortStates[portKey];
            if (port != null)
                this._updateDataFromPort(portKey, port.State, port.LastStateStatus == StateStatus.Valid);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    get columnsWidth(): { [keys: string]: number } {
        let widths = {};
        _.forEach(this.props.WidgetData.Columns, column => widths[column.Key] = column.Width);
        return widths;
    }
    // ---------------------------------------------------------------------------------------------------
    updateColumns(
        settings: TableSettings = this.props.WidgetData,
        columns: ColumnSettings[] = this.props.WidgetData.Columns,
        enumSettings: IEnumSettings[] = this.props.WidgetData.EnumSettings,
        locMap: LocalizationMap = this.props.LocalizationMap,
        updateColvis: boolean = false,
        forceUsedExternalColumns: boolean = false,
        dropdownMenuSettings: IDropdownMenuSettings[] = this.props.WidgetData.DropdownMenuSettings, //todo nested menu items comming from the settings
    ) {
        // Initialize `_colvis` (if needed)
        if (!isDefined(this._colvis)) {
            try {
                // Read local storage for columns visibility.
                this._colvis = JSON.parse(localStorage.getItem(this.colVisStorageKey));
            }
            catch (ex) {
                DebugEx.TraceError(ex);
            }
            // If parse failed, initialize to empty.
            if (!isDefined(this._colvis))
                this._colvis = {};
        }

        // Check if we have port data that we can use directly and override our staffs
        if (!forceUsedExternalColumns &&
            _.has(this.props.PortsStates, TableKeys.CustomColumns) &&
            this.props.PortsStates[TableKeys.CustomColumns].LastStateStatus == StateStatus.Valid) {
            let cols = this.props.PortsStates[TableKeys.CustomColumns].State as ColumnSettings[];
            let enums: IEnumSettings[] = [];

            // Update enum settings.
            _.forEach(
                _.filter(cols, v =>
                    v.Type == ColumnType.enum &&
                    isDefined(v.EnumSettings)
                ),
                v => {
                    v.EnumSettings.key = v.Key;
                    enums.push(v.EnumSettings);
                });
            columns = cols;
            enumSettings = enums;
        }

        // Update `_colvis` (if needed)
        if (updateColvis) {
            // Update this._colvis based on Hidden event of columns
            _.forEach(columns, v => {
                this._colvis[v.Key] = !v.Hidden;
            
                // Use columns to update `colvis`
                if (this.props.width < AutoHiddableWidth) {
                    // widget's width is not enough; hide `AutoHiddable` columns           
                    if (v.Hidden == false) {
                        if (v.AutoHiddable)
                            this._colvis[v.Key] = false;
                        else
                            this._colvis[v.Key] = true;
                    }
                    
                }
            });

            // Store columns visibility in local storage.
            localStorage.setItem(
                this.colVisStorageKey,
                JSON.stringify(this._colvis)
            );
        }

        // clone EnumSettings
        const _enumSettings = _.cloneDeep(enumSettings);
        const _settings = this.state.settings;
        const _dropdownMenuSettings = _.cloneDeep(dropdownMenuSettings);

        // Map columns from settings to the table's one.
        const _columns = _.map(columns, v => {
            // Try to translate the column's title.
            let title = locMap[LocalizationLabelKey.column + v.Key];
            if (isStringEmpty(title))
                title = v.Title;

            let tooltip = v.Tooltip;
            // Try to translate the column's tooltip.
            tooltip = locMap[LocalizationLabelKey.tooltip + v.Key];

            // No translation? Set again the default.
            if (isStringEmpty(tooltip))
                tooltip = v.Tooltip;

            let header = (isStringEmpty(tooltip)) ? title : (data, column) => <Tippy placement="bottom" content={tooltip}><div>{title}</div></Tippy>

            const oldColVisValue = this._colvis[v.Key] ?? true;
            const column: ReactTableColumn = {
                Header: header,
                title: title,
                accessor: v.JsonName,
                id: v.JsonName,
                key: v.Key,
                isPrimaryKey: v.IsPrimaryKey,
                show: !v.Hidden && oldColVisValue,
                filterable: v.Filtering,
                sortable: v.Sortable,
                editable: _settings.Editable && !v.NonEditable,
                type: v.Type,
                buttonIcon: v.Icon,
                buttonTooltip: tooltip,
                pivotBy: v.PivotBy && settings.PivotSupport,
                checkedIcon: v.CheckedIcon,
                uncheckedIcon: v.UncheckedIcon,
                backgroundColorAccessor: v.BackgroundColorAccessor,
                fontColorAccessor: v.FontColorAccessor,
                styleAccessor: v.StyleAccessor,
                visibleInAdd: v.VisibleInAdd,
                progressIsCircle: v.progressIsCircle,
                progressColor: v.progressColor,
                progressWidth: v.progressWidth,
                progressShowValue: v.progressShowValue,
                progressTrailColor: v.progressTrailColor,
                progressTrailWidth: v.progressTrailWidth,
                isMandatory: v.IsMandatory,
                headerBackgroundColor: v.HeaderBackgroundColor,
                pivotSettings: v.PivotSettings
            };

            if (v.Type === ColumnType.enum)
                this._prepareEnumColumn(column, _enumSettings, locMap);

            /**
             * Store dropdown menu column in order to pass them through props
             */
            if (v.Type === ColumnType.dropdown) {
                let dropdownItems = _.filter(dropdownMenuSettings, (el) => el.key == v.Key);
                column.dropdownMenuMapping = dropdownItems
            }

            return column;
        });

        if (settings.ShowEditRow) {
            let tooltip = locMap[LocalizationLabelKey.editRowButton];

            // No translation? Set again the default.
            if (isStringEmpty(tooltip))
                tooltip = editRowTooltip;

            _columns.push({
                Header: "Edit",
                accessor: "table_widget_edit_row",
                id: "table_widget_edit_row",
                key: "table_widget_edit_row_key",
                show: true,
                filterable: false,
                sortable: false,
                editable: false,
                type: ColumnType.editRow,
                buttonIcon: IconType.edit,
                buttonTooltip: tooltip,
                pivotBy: false,
                checkedIcon: null,
                uncheckedIcon: null
            });
        }

        this.setState({
            columns: _columns,
            enumSettings: _enumSettings,
            dropdownMenuSettings: _dropdownMenuSettings
        });
    }
    // ---------------------------------------------------------------------------------------------------
    private _prepareEnumColumn(column: ReactTableColumn, enumSettings: IEnumSettings[], localizationMap: LocalizationMap): void {
        const enumEntry = _.find(enumSettings, (e) => e.key == column.key);
        if (!isDefined(enumEntry)) {
            DebugEx.TraceCritical("Should not be here");
            return;
        }

        // try translate values
        column.enumMapping = _.cloneDeep(enumEntry.data);
        column.enumMulti = enumEntry.multi;
        column.enumCreatable = enumEntry.creatable;
        column.enumUseDialog = enumEntry.useDialog;
        _.forEach(enumEntry.data, (el, index) => {
            if (!isDefined(el.key))
                return;
            const title = localizationMap[LocalizationLabelKey.enumValue + enumEntry.key + '_' + el.key];
            if (!isStringEmpty(title))
                column.enumMapping[index].label = title;
        });

        return;
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const { wrapTextEnabled, filterEnabled, columns: Columns } = this.state;
        const settings = this.props.WidgetData;
        const _settings = this.state.settings;

        let toolbarButtons = ToolbarButton.none;
        if (settings.ShowWrapText)
            toolbarButtons |= ToolbarButton.wrapText;
        if (settings.ShowColVis)
            toolbarButtons |= ToolbarButton.colvis;
        if (settings.PivotSupport)
            toolbarButtons |= ToolbarButton.pivotBy;
        if (settings.filterable)
            toolbarButtons |= ToolbarButton.filter;
        if (settings.DownloadExcel)
            toolbarButtons |= ToolbarButton.excel;
        if (_settings.ShowAddRow && _settings.Editable)
            toolbarButtons |= ToolbarButton.addrow;
        if (settings.CSVImport)
            toolbarButtons |= ToolbarButton.CSVImport;

        // Custom button for sending the selected rows.
        const sendSelectedButton = settings.SendSelectedButton;
        const selectionActionIcon: ISelectionActionIcon = {
            elementIcon: sendSelectedButton.icon,
            tooltip: sendSelectedButton.tooltip
        };

        //Map custom toolbar-icons tooltips with translations
        _.forEach(settings.ToolbarIcons, toolbarIcon => {
            toolbarIcon.tooltip = this.props.LocalizationMap[LocalizationLabelKey.tooltip + "toolbar-icon" + toolbarIcon.id];
        });

        // workaround for showing the first page of table
        let rootKey = 'react-table-wrapper';
        if (settings.ServerSideProcessing && this._reset == true) {
            this._reset = false;
            rootKey = getRandomKey();
        }

        return (
            <div
                key={rootKey}
                style={{
                    overflow: 'hidden',
                    height: this.props.height,
                    padding: defaultWidgetContainerPadding
                }}>
                <ReactTable
                    localizationKey={this.props.NodeKey}
                    localizationMap={this.props.LocalizationMap}
                    columns={Columns}
                    height={this.props.height}
                    selectable={_settings.Selectable}
                    selectAllTooltip={_settings.SelectAllTooltip}
                    selectAllMode={_settings.SelectAllMode}
                    singleSelection={_settings.SelectableTypeRadio}
                    autoSend={settings.AutoSend}
                    editable={_settings.Editable}
                    filterEnabled={settings.filterable && filterEnabled}
                    wrapTextEnabled={wrapTextEnabled}
                    data={_settings.Editable ? _.cloneDeep(this._tableData) : this._tableData}
                    toolbarButtons={toolbarButtons}
                    toolbarIcons={settings.ToolbarIcons}
                    toolbarIconSize={settings.ToolbarIconSize}
                    toolbarHeight={settings.ToolbarHeight}
                    resizable={settings.DynamicColumnsWidth}
                    defaultPageSize={settings.PageListSettings.DefaultSelected}
                    pageSizeOptions={settings.PageListSettings.PageList}
                    currency={settings.PriceCurrency}
                    defaultImgUrl={settings.DefaultImageURL}
                    imgWidth={settings.ImgWidth}
                    imgHeight={settings.ImgHeight}
                    objectFit={settings.ObjectFit}
                    dateFormat={settings.DateFormat}
                    timeFormat={settings.timeFormat}
                    showSubComponent={settings.ShowSubComponent}
                    onlyHiddenColumnsInSubcomponent={settings.OnlyHiddenColumnsInSubcomponent}
                    onSaveEditedRows={(this.props.WidgetData.OnSaveSendEverything) ? null : this.onSaveEditedRows}
                    onSaveRows={(this.props.WidgetData.OnSaveSendEverything) ? this.onSaveEditedRows : null}
                    onSaveAddedRows={this.onSaveAddedRows}
                    onSendSelected={this.onSendSelected}
                    onRowSelect={this.onRowSelect}
                    onRowUpdate={this.onRowUpdate}
                    onColvisChange={this.handleColvisChange}
                    onFilterClick={this.handleFilterClick}
                    onWrapTextClick={this.handleWrapTextClick}
                    onButtonClick={this.updateOutputPort}
                    onCellClick={this.onCellClick}
                    onPageChange={this.onPageChange}
                    onToolbarIconClick={this.onToolbarIconClick}
                    onDropdownMenuItemClick={this.onDropdownMenuItemClick}
                    pivotSettings={settings.PivotSettings}
                    pagingSupport={settings.PagingSupport}
                    addDuplicateRowPolicy={settings.AddDuplicateRowPolicy}
                    selectionActionIcon={selectionActionIcon}
                    excelFileName={settings.DownloadExcelOptions?.fileName}
                    theme={settings.Theme}
                    serverSideProcessing={settings.ServerSideProcessing}
                    onFetchDataFromServer={this.onFetchDataFromServer}
                    totalPages={this._pages}
                    loading={this.state.loading}
                    csvImportSettings={this.props.WidgetData.CSVImportSettings}
                />
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    onSaveEditedRows = (rows: any[]) => this.props.SendPortEvent(TableKeys.EditDataPortKey, JSON.stringify(rows))
    // ---------------------------------------------------------------------------------------------------
    onPageChange = (data: PageChange) => this.props.SendPortEvent(TableKeys.onPageChange, JSON.stringify(data))
    // ---------------------------------------------------------------------------------------------------
    onVisibilityChange = (data: { [key: string]: boolean }) => this.props.SendPortEvent(TableKeys.onVisibilityChange, JSON.stringify(data))
    // ---------------------------------------------------------------------------------------------------
    onDropdownMenuItemClick = (data: DropDownItem) => this.props.SendPortEvent(TableKeys.onDropdownMenuItemClick, JSON.stringify(data))
    // ---------------------------------------------------------------------------------------------------
    onSaveAddedRows = (rows: any[]) => this.props.SendPortEvent(TableKeys.AddDataPortKey, JSON.stringify(rows))
    // ---------------------------------------------------------------------------------------------------
    onSendSelected = (rows: any[]) => this.props.SendPortEvent(TableKeys.SelectDataPortKey, JSON.stringify(rows))
    // ---------------------------------------------------------------------------------------------------
    onRowSelect = (rows: number | string[]) => {
        if (this.props.WidgetData.AutoSend)
            this.props.SendPortEvent(TableKeys.SelectDataPortKey, JSON.stringify(rows));
    }
    // ---------------------------------------------------------------------------------------------------
    onCellClick = (row: any, columnJsonName: string, eventSrc: string = onCellClickSrcKeys.Cell) => {
        if (isDefined(row)) {
            this.props.SendPortEvent(TableKeys.onCellClick, JSON.stringify({
                row: row,
                columnJsonName: columnJsonName,
                eventSrc: eventSrc
            }));
        }
    }
    // ---------------------------------------------------------------------------------------------------
    onToolbarIconClick = (iconInfo: ToolbarIconInfo) => this.props.SendPortEvent(TableKeys.onToolbarIconClick, iconInfo.id)
    // ---------------------------------------------------------------------------------------------------
    onRowUpdate = (index: number, values: any) => {
        this.props.SendPortEvent(TableKeys.onRowUpdate, JSON.stringify({
            index: index,
            values: values
        } as IOnRowUpdate));
    }
    // ---------------------------------------------------------------------------------------------------
    handleFilterClick = (enabled: boolean) => {
        this.setState({ filterEnabled: enabled }, () => this.updateSessionStorage());
    }
    // ---------------------------------------------------------------------------------------------------
    handleWrapTextClick = (enabled: boolean) => {
        this.setState({ wrapTextEnabled: enabled }, () => this.updateSessionStorage());
    }
    // ---------------------------------------------------------------------------------------------------
    handleColvisChange = (key: string) => {
        const oldValue = this._colvis[key] ?? true;
        // Toggle column's visibility.
        this._colvis[key] = !oldValue;

        // Update local storage.
        localStorage.setItem(
            this.colVisStorageKey,
            JSON.stringify(this._colvis)
        );

        // Send the event that the visibility change
        this.onVisibilityChange(this._colvis);
    }
    // ---------------------------------------------------------------------------------------------------
    private get colVisStorageKey(): string {
        return this.props.ThingKey + __COLUMNS_VISIBILITY_KEY__;
    }
    // ---------------------------------------------------------------------------------------------------
    updateSessionStorage() {
        // Update browser's session storage.
        this._stateStorage.SetState(this.props.ThingKey, {
            filterEnabled: this.state.filterEnabled,
            wrapTextEnabled: this.state.wrapTextEnabled
        } as StorageData);
    }
    // ---------------------------------------------------------------------------------------------------
    onFetchDataFromServer = (request: IRequestServerData) => {
        this.setState({ loading: true }, () => {
            this.props.SendPortEvent(TableKeys.FetchData, JSON.stringify(request))
        });
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: PartialIWidgetThingsProps<TableData>) {
        const newProps = this.props;

        const widthChanged = !_.isEqual(prevProps.width, newProps.width);
        const newWidgetData = !_.isEqual(prevProps.WidgetData, newProps.WidgetData);
        const locChanged = !_.isEqual(prevProps.LocalizationMap, newProps.LocalizationMap);

        if (newWidgetData || widthChanged || locChanged) {
            this.updateColumns(
                newProps.WidgetData,
                newProps.WidgetData.Columns,
                (newWidgetData) ? newProps.WidgetData.EnumSettings : this.state.enumSettings,
                newProps.LocalizationMap,
                newWidgetData || widthChanged,
                false,
                (newWidgetData) ? newProps.WidgetData.DropdownMenuSettings : this.state.dropdownMenuSettings
            );
            if (newWidgetData) {
                this._updateUsingTimestamp(newProps);
                this.setState({
                    // Update the runtime settings.
                    settings: {
                        Editable: newProps.WidgetData.Editable,
                        ShowAddRow: newProps.WidgetData.ShowAddRow,
                        Selectable: newProps.WidgetData.Selectable,
                        SelectAllTooltip: newProps.WidgetData.SelectAllTooltip,
                        SelectAllMode: newProps.WidgetData.SelectAllMode,
                        SelectableTypeRadio: newProps.WidgetData.SelectableTypeRadio
                    }
                });
            }
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _updateUsingTimestamp(props: PartialIWidgetThingsProps<TableData>) {
        if (!isDefined(props.PortsStates))
            return;

        // port keys to check
        const portKeysToCheck = [
            // data port
            props.WidgetData.Key,
            // insert or update
            TableKeys.InsertOrUpdatePortKey,
            ..._.map(props.WidgetData.EnumSettings, e => e.key)
        ];

        // foreach found port (order matters)
        _.forEach(
            // sort by timestamp
            _.sortBy(
                // map port keys to ports
                _.map(
                    // detect used ports
                    _.filter(portKeysToCheck,
                        (key) => {
                            // try get port
                            const port = props.PortsStates[key];
                            // try get timestamp
                            const timestampExists = isDefined(port) && isDefined(port.Timestamp);
                            // process port if timestamp exists
                            return timestampExists;
                        })
                    , (key) => {
                        // return tuple[portKey,portState info]
                        return [key, props.PortsStates[key]];
                    })
                , [(port: [string, PortData]) => {
                    return port[1].Timestamp;
                }]),
            (port: [string, PortData]) => {
                this._updateDataFromPort(port[0],
                    port[1].State,
                    port[1].LastStateStatus == StateStatus.Valid,
                    props);
            });
    }
    // ---------------------------------------------------------------------------------------------------
    private _updateDataFromPort(portKey: string, portState: any, isValid: boolean, props: PartialIWidgetThingsProps<TableData> = this.props) {
        let data: any[] = [];
        try {
            const invalidData = () => {
                this._tableData = [];
                this._pages = null;
                this._reset = null;
                this.setState({
                    loading: false,
                    dataVersion: this.state.dataVersion + 1
                });
                return;
            };

            const convertDates = () => {
                if (_.isArray(data)) {
                    // convert date-time to javascript date
                    _.forEach(props.WidgetData.Columns, element => {
                        if (element.Type == ColumnType.datetime ||
                            element.Type == ColumnType.date) {
                            _.forEach(data, row => {
                                if (_.has(row, element.JsonName) && isDefined(row[element.JsonName])) {
                                    row[element.JsonName] = moment.utc(row[element.JsonName]).toDate();
                                }
                            });
                        }
                    });
                } else {
                    data = [];
                }
            };

            if (portKey == TableKeys.DataPortKey || portKey == props.WidgetData.Key) {
                if (!isValid) {
                    invalidData();
                }

                data = portState as any[];
                convertDates();
            } else if (portKey == TableKeys.ServerData) {
                if (!isValid) {
                    invalidData();
                }

                // fetched data & pages from server
                let serverData = portState as IServerData;
                data = serverData?.rows;
                this._pages = serverData?.pages;
                this._reset = serverData?.reset;

                convertDates();
            } else if (portKey == TableKeys.InsertOrUpdatePortKey) {
                if (!isValid) {
                    return;
                }
                data = portState as any[];
                // check if data is JArray
                if (_.isArray(data)) {
                    const primaryKeys: string[] = _.map(_.filter(props.WidgetData.Columns,
                        (v) => { return v.IsPrimaryKey; }), (v) => { return v.JsonName; });

                    const tableData = _.cloneDeep(this._tableData);
                    _.forEach(data, (dRow) => {
                        let columnsForChange: any[];
                        let updateRowIdx: number;
                        let updateData: boolean = false;
                        columnsForChange = [];
                        // take only keys that exist
                        _.forEach(props.WidgetData.Columns, (column) => {
                            if (_.has(dRow, column.JsonName))
                                columnsForChange.push(column.JsonName);
                        });
                        if (_.isArray(primaryKeys) && !_.isEmpty(primaryKeys)) {
                            _.every(tableData, (tdRow, tdKey) => {
                                // check keys are correct
                                updateData = _.every(primaryKeys, (pk) => {
                                    // break if primary keys has different value Or doesn't exist in this row of tableData
                                    if (_.has(tdRow, pk) && dRow[pk] == tdRow[pk]) {
                                        updateRowIdx = tdKey;
                                        return true;
                                    } else
                                        return false;
                                });
                                return !updateData;
                            });
                        }
                        const dataToChange = _.pick(dRow, columnsForChange);
                        if (updateData)
                            // update
                            _.assign(tableData[updateRowIdx], dataToChange);
                        else
                            // insert
                            tableData.push(dataToChange);
                    });
                    data = tableData;
                } else {
                    // Avoid any changes.
                    // We don't support non-array type of data
                    return;
                }
            }
            else if (portKey == TableKeys.Settings) {
                if (!isValid) return;
                // Override any provided settings.
                this.setState({
                    settings: _.merge({},
                        this.state.settings,
                        portState as TableSettings)
                }, () => this.updateColumns(
                    props.WidgetData,
                    props.WidgetData.Columns,
                    this.state.enumSettings,
                    props.LocalizationMap,
                    false,
                    false,
                    this.state.dropdownMenuSettings
                ));
                // Break out.
                return;
            }
            else if (portKey == TableKeys.CustomColumns) {
                if (!isValid) return;

                let cols = portState as ColumnSettings[];
                let enums: IEnumSettings[] = [];

                // Update enum settings.
                _.forEach(
                    _.filter(cols, v =>
                        v.Type == ColumnType.enum &&
                        isDefined(v.EnumSettings)
                    ),
                    v => {
                        v.EnumSettings.key = v.Key;
                        enums.push(v.EnumSettings);
                    });

                // Create custom columns.
                this.updateColumns(
                    props.WidgetData,
                    cols,
                    enums,
                    props.LocalizationMap,
                    true,
                    true
                );
                // Break out.
                return;
            }
            else if (portKey == TableKeys.onCellClick
                || portKey == TableKeys.EditDataPortKey
                || portKey == TableKeys.AddDataPortKey
                || portKey == TableKeys.SelectDataPortKey
                || portKey == TableKeys.onToolbarIconClick
                || portKey == TableKeys.onPageChange
                || portKey == TableKeys.onDropdownMenuItemClick
                || portKey == TableKeys.FetchData
                || portKey == TableKeys.onVisibilityChange) {
                // Do nothing
                return;
            } else {
                // Check if port corresponds to enum column key
                const enumEntryIndex = _.findIndex(props.WidgetData.EnumSettings, (e) => e.key == portKey);
                if (enumEntryIndex === -1)
                    return;

                if (!isValid)
                    return;

                // parse json
                const enumData: EnumType[] = portState;

                // update state
                this.setState((s: TableState) => {
                    // try get column
                    const index = _.findIndex(s.columns, (col) => col.key === portKey);
                    if (index === -1) {
                        DebugEx.TraceCritical("Should not be here!", portKey);
                        return s;
                    }

                    // update enum settings
                    s.enumSettings[enumEntryIndex].data = enumData;
                    // update columns (unfortunately we need deep clone to force ReactTable re-draw)
                    s.columns = _.cloneDeep(s.columns);
                    this._prepareEnumColumn(s.columns[index], s.enumSettings, props.LocalizationMap);

                    return s;
                });
                return;
            }
            this._tableData = _.cloneDeep(data);
            this.setState({
                loading: false,
                dataVersion: this.state.dataVersion + 1
            });
        }
        catch (ex) {
            DebugEx.TraceError(ex);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    updateOutputPort = (portKey: string, row: any) => {
        this.props.SendPortEvent(portKey, JSON.stringify(row));
    }
    // ---------------------------------------------------------------------------------------------------
}
// ---------------------------------------------------------------------------------------------------
interface TableWidgetSettingsState {
    WidgetData: TableData;
}

class TableWidgetSettings extends React.Component<IWidgetSettingsProps<TableData>, TableWidgetSettingsState> {
    constructor(props: IWidgetSettingsProps<TableData>) {
        super(props);
        this.state = { WidgetData: _.cloneDeep(this.props.WidgetData) };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: IWidgetSettingsProps<TableData>) {
        if (!_.isEqual(this.props.WidgetData, prevProps.WidgetData))
            this.setState({ WidgetData: _.cloneDeep(this.props.WidgetData) });
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<TableData>[] = [
            {
                name: "Modes",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Editable',
                        tooltip: 'If enabled, by double clicking on any non Button field will enable the inline editor. \
                                Once the inline editor is shown, you can either save or cancel the modification. \
                                You can send the modified rows by clicking/tapping the Save Changes button or discard them with the Discard Changes button that are shown above table.',
                        dataPath: "Editable"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Selectable',
                        tooltip: 'If enabled, checkboxes will be shown for each row and a Send Selected button above table. You can send the selected rows by clicking/tapping the button.',
                        dataPath: "Selectable"
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Select All Mode',
                        tooltip: "Specify the symbol to use at Currency fields. Available options: USD, GBP and Euro",
                        dataPath: "SelectAllMode",
                        items: [
                            { value: SelectAllMode.All, label: "All" },
                            { value: SelectAllMode.Filtered, label: "Filtered" },
                        ],
                        isHidden: d => !d.Selectable,
                    },
                    {
                        type: SettingsType.String,
                        label: 'Select All Tooltip',
                        tooltip: 'Text to appears on select all checkbox hover.',
                        dataPath: 'SelectAllTooltip',
                        isHidden: d => !d.Selectable,
                    },
                    {
                        type: SettingsType.Group,
                        name: 'Send Selected Button',
                        isHidden: d => !d.Selectable,
                        tooltip: 'tooltip',
                        elements: [
                            {
                                type: SettingsType.Icon,
                                label: 'Icon',
                                tooltip: 'The Font Awesome icon of button',
                                dataPath: 'SendSelectedButton.icon',

                            },
                            {
                                type: SettingsType.String,
                                label: 'Tooltip',
                                tooltip: 'Text to appears on hover',
                                dataPath: 'SendSelectedButton.tooltip'
                            }
                        ]
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Selectable Type Radio',
                        tooltip: 'This field is shown when Selectable option is enabled and allows user to choose only one row.',
                        dataPath: "SelectableTypeRadio",
                        isHidden: (d) => !d.Selectable
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Auto Send Selection',
                        tooltip: 'This field is shown when Selectable option is enabled and it will send automatically every selection if it is enabled.',
                        dataPath: "AutoSend",
                        isHidden: (d) => !d.Selectable
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Filterable',
                        tooltip: "If enabled, a Filter button will be shown above table. By clicking/tapping the button shows up a toolbar where you can search and filter table's data.",
                        dataPath: "filterable"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Pivot Support',
                        tooltip: "Pivoting the table will group records together based on their accessed values and allow the rows in that group to be expanded underneath it. \
                                To pivot, pass an array of columnID's to pivotBy. \
                                Remember, a column's id is either the one that you assign it (when using a custom accessors) or its accessor string.",
                        dataPath: "PivotSupport"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Wrap Text',
                        tooltip: 'If enabled, the toolbar will provide an option for wrapping text.',
                        dataPath: "ShowWrapText"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Column Visibility',
                        tooltip: 'If enabled, the toolbar will provide an option for column visibility.',
                        dataPath: "ShowColVis"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Server-Side Processing',
                        tooltip: 'Handles the pagination, sorting, and filtering on the server',
                        dataPath: 'ServerSideProcessing'
                    }
                ]
            },
            {
                name: "Pivot",
                elements: [
                    {
                        type: SettingsType.DropDown,
                        label: 'Type',
                        dataPath: "PivotSettings.Type",
                        items: [
                            { value: PivotValueSelectionType.Avg, label: "Average" },
                            { value: PivotValueSelectionType.Max, label: "Maximum" },
                            { value: PivotValueSelectionType.Min, label: "Minimum" },
                            { value: PivotValueSelectionType.Sum, label: "Sum" },
                            { value: PivotValueSelectionType.Selected, label: "Selected" },
                        ],
                        isHidden: (d) => !d.PivotSupport
                    },
                    {
                        type: SettingsType.String,
                        label: 'Selected JSON field',
                        dataPath: "PivotSettings.SelectedBooleanField",
                        isHidden: (d) => !d.PivotSupport || d.PivotSettings.Type != PivotValueSelectionType.Selected,
                    }
                ]
            },
            {
                name: "Columns Settings",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: 'Columns',
                        tooltip: "Sets the features of columns.",
                        dataPath: "Columns",
                        dialogStaticSize: { w: 0.95, h: 0.95 },
                        itemsInfo: [
                            {
                                label: "Primary Key",
                                type: SettingsType.Boolean,
                                tooltip: 'The primary key consists of one or more columns whose data contained within is used to uniquely identify each row in the table.',
                                dataPath: "IsPrimaryKey",
                                isHiddenCell: (data, row, index) =>
                                    row.Type == ColumnType.button ||
                                    row.Type == ColumnType.color ||
                                    row.Type == ColumnType.img
                            },
                            {
                                label: "JSON Name",
                                type: SettingsType.String,
                                tooltip: "Field's name.",
                                dataPath: "JsonName",
                                isHiddenCell: (data, row, index) => row.Type == ColumnType.button || row.Type == ColumnType.dropdown,
                                isUnique: true
                            },
                            {
                                label: "Table Title",
                                type: SettingsType.String,
                                tooltip: "Column's title.",
                                dataPath: "Title"
                            },
                            {
                                label: "Type",
                                type: SettingsType.DropDown,
                                tooltip: "Field's type. Available types: Text, Boolean, Number, Price, Color, Percentage, Datetime, Button, Enum and Image. \
                                        Enum fields can have multiple values too. If multi selection is enabled for a specific field., the expected JSON value is 'value1,value2,value3'.",
                                dataPath: "Type",
                                items: [
                                    { value: ColumnType.text, label: "Text" },
                                    { value: ColumnType.boolean, label: "Boolean" },
                                    { value: ColumnType.number, label: "Number" },
                                    { value: ColumnType.price, label: "Price" },
                                    { value: ColumnType.color, label: "Color" },
                                    { value: ColumnType.percentage, label: "Percentage" },
                                    { value: ColumnType.datetime, label: "Datetime" },
                                    { value: ColumnType.date, label: "Date" },
                                    { value: ColumnType.time, label: "Time" },
                                    { value: ColumnType.button, label: "Button" },
                                    { value: ColumnType.enum, label: "Enum" },
                                    { value: ColumnType.img, label: "Image" },
                                    { value: ColumnType.progress, label: "Progress" },
                                    { value: ColumnType.dropdown, label: "Dropdown" },
                                ]
                            },
                            {
                                label: "Mandatory",
                                type: SettingsType.Boolean,
                                tooltip: "Define if column is mandatory.",
                                dataPath: "IsMandatory",
                                isEnabled: (data, row, index) =>
                                    !(row.Type == ColumnType.button ||
                                        row.Type == ColumnType.color),
                            },
                            {
                                label: "Sortable",
                                type: SettingsType.Boolean,
                                tooltip: "Whether to support or not sorting.",
                                dataPath: "Sortable",
                                isEnabled: (data, row, index) =>
                                    !(row.Type == ColumnType.button ||
                                        row.Type == ColumnType.color ||
                                        row.Type == ColumnType.img),
                            },
                            {
                                label: "Filtering",
                                type: SettingsType.Boolean,
                                tooltip: "Whether to support or not filtering.",
                                dataPath: "Filtering",
                                isEnabled: (data, row, index) =>
                                    !(row.Type == ColumnType.button ||
                                        row.Type == ColumnType.color ||
                                        row.Type == ColumnType.img),
                            },
                            {
                                label: "Hidden",
                                type: SettingsType.Boolean,
                                tooltip: "Whether to hide or not the current column. Data emitted from button click/tap will contain column's value.",
                                dataPath: "Hidden",
                                isEnabled: (data, row, index) =>
                                    !(row.Type == ColumnType.button ||
                                        row.Type == ColumnType.color),
                            },
                            {
                                label: "Non editable",
                                type: SettingsType.Boolean,
                                tooltip: 'Prevent editing the current column if Editable is enabled.',
                                dataPath: "NonEditable",
                                isEnabled: (data, row, index) =>
                                    !(row.Type == ColumnType.button ||
                                        row.Type == ColumnType.progress ||
                                        row.Type == ColumnType.img),
                            },
                            {
                                label: "Visible in Add",
                                type: SettingsType.Boolean,
                                tooltip: 'Visible in add dialog.',
                                dataPath: "VisibleInAdd",
                                isHiddenCell: (data, row, index) => !data.ShowAddRow,
                                isHidden: (data) => !data.ShowAddRow
                            },
                            {
                                label: "Auto Hidable",
                                type: SettingsType.Boolean,
                                tooltip: 'Hides the current column when the widget has a width of less than 500 pixels.',
                                dataPath: "AutoHiddable",
                                isEnabled: (data, row, index) => row.Type != ColumnType.button,
                            },
                            {
                                label: "Pivoting",
                                type: SettingsType.Boolean,
                                dataPath: "PivotBy",
                                tooltip: "Column for Pivoting (TreeView)",
                                isEnabled: (data, row, index) => row.Type == ColumnType.text,
                            },
                            {
                                label: "Tooltip",
                                type: SettingsType.String,
                                tooltip: "Used only when field's type is Button.",
                                dataPath: "Tooltip",
                                //  isHiddenCell: (data, row, index) => row.Type != ColumnType.button,
                            },
                            {
                                label: "Background Color",
                                tooltip: "The accessor (property name) in JSON which sets the background color of the cell",
                                type: SettingsType.String,
                                dataPath: "BackgroundColorAccessor"
                            },
                            {
                                label: "Font Color",
                                tooltip: "The accessor (property name) in JSON which sets the font color of the cell",
                                type: SettingsType.String,
                                dataPath: "FontColorAccessor"
                            },
                            {
                                label: 'Style',
                                tooltip: 'The accessor (property name) in JSON which sets the style of the cell',
                                type: SettingsType.String,
                                dataPath: 'StyleAccessor'
                            },
                            {
                                label: "Header Background Color",
                                type: SettingsType.ColorPicker,
                                tooltip: "Define specific color for a column header.",
                                dataPath: "HeaderBackgroundColor"
                            },
                            {
                                label: "Icon Type",
                                type: SettingsType.DropDown,
                                tooltip: "Use only when field's type is Button. Available types: Add, Delete, Edit, Info.",
                                dataPath: "Icon",
                                isHiddenCell: (data, row, index) => row.Type != ColumnType.button,
                                items: [
                                    { value: IconType.add, label: "Add" },
                                    { value: IconType.delete, label: "Delete" },
                                    { value: IconType.edit, label: "Edit" },
                                    { value: IconType.info, label: "Info" },
                                    { value: IconType.custom, label: "Custom Icon" },
                                ],
                            },
                            {
                                label: "Checked Icon",
                                type: SettingsType.Icon,
                                tooltip: "Used only when field's type is Boolean. Select the icon desired on checked.",
                                dataPath: "CheckedIcon",
                                isHiddenCell: (data, row, index) => !(row.Type == ColumnType.boolean ||
                                    (row.Type == ColumnType.button && row.Icon == IconType.custom) || row.Type === ColumnType.dropdown),
                            },
                            {
                                label: "Unchecked Icon",
                                type: SettingsType.Icon,
                                tooltip: "	Used only when field's type is Boolean. Select the icon desired on unchecked.",
                                dataPath: "UncheckedIcon",
                                isHiddenCell: (data, row, index) => row.Type != ColumnType.boolean,
                            }
                        ],
                        createNewItem: (data) => {
                            // WARNING:
                            // The new width is computed with this way because the added
                            // column has always text type. If this will change and a button or
                            // a boolean type is added set lower value.

                            // Compute new column's width.
                            let newWidth: number = 100 / (data.Columns.length + 1);

                            // Reduce width of existing columns with the added one.
                            _.forEach(data.Columns, (column, index) => {
                                column.Width -= (newWidth * column.Width) / 100;
                            });
                            return _.defaults(
                                {
                                    Key: getRandomSmallKey(),
                                    JsonName: getRandomSmallKey(),
                                    Title: "New column",
                                    Width: newWidth,
                                },
                                DefaultColumnSettings
                            );
                        },
                        onSaveUpdate: (newData) => {
                            // column keys that we deleted or their type is not anymore `Column.enum`
                            // initiliaze with all keys
                            const removedKeys = _.map(newData.EnumSettings, (setting) => setting.key);
                            const removedDropdownItemsKeys = _.map(newData.DropdownMenuSettings, (setting) => setting.key);

                            // for each column
                            _.forEach(newData.Columns, (col) => {

                                // check if enum entry
                                if (col.Type == ColumnType.enum) {
                                    // try get index
                                    const index = removedKeys.indexOf(col.Key);

                                    // check if new entry
                                    if (index === -1) {
                                        newData.EnumSettings.push({
                                            key: col.Key,
                                            data: [],
                                            multi: false,
                                            creatable: false,
                                            useDialog: false
                                        });
                                    }
                                    else
                                        // update `removedKeys`
                                        _.pull(removedKeys, col.Key);
                                }

                                // check if dropdown entry
                                if (col.Type == ColumnType.dropdown) {
                                    // try get index
                                    const index = removedDropdownItemsKeys.indexOf(col.Key);

                                    // check if new entry
                                    if (index === -1) {
                                        newData.DropdownMenuSettings.push({
                                            key: col.Key,
                                            dropdownMenuItems: [],
                                        });
                                    }
                                    else
                                        // update `removedKeys`
                                        _.pull(removedDropdownItemsKeys, col.Key);
                                }
                            });

                            // for each removed key => remove enum entry
                            _.forEach(removedKeys, (key) => {
                                // try get index
                                const index = _.findIndex(newData.EnumSettings, (e) => e.key == key);
                                if (index !== -1)
                                    // remove entry
                                    newData.EnumSettings.splice(index, 1);
                                else
                                    // trace error
                                    DebugEx.TraceCritical("Should not be here!");
                            });

                            // for each removed key => remove dropdown menu entry
                            _.forEach(removedDropdownItemsKeys, (key) => {
                                // try get index
                                const index = _.findIndex(newData.DropdownMenuSettings, (e) => e.key == key);
                                if (index !== -1)
                                    // remove entry
                                    newData.DropdownMenuSettings.splice(index, 1);
                                else
                                    // trace error
                                    DebugEx.TraceCritical("Should not be here!");
                            });

                            return newData;
                        }
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Subcomponent',
                        tooltip: 'Whether to show subcomponent or not.',
                        dataPath: "ShowSubComponent"
                    },
                    {
                        type: SettingsType.Boolean,
                        dataPath: 'OnlyHiddenColumnsInSubcomponent',
                        label: 'Only Hidden Columns in Subcomponent',
                        tooltip: 'The columns that are hidden can be made visible through the subcomponent table',
                        isHidden: d => !d.ShowSubComponent
                    }
                ]
            },
            // ------------------------------------------------------------  +++ EnumMappings
            {
                name: "Enum Settings",
                elementsFunc: (data) => _(data.EnumSettings)
                    .filter((d: IEnumSettings) => {
                        const column = _.find(data.Columns, (col) => col.Key == d.key);
                        return isDefined(column);
                    })
                    .map((d, i) => {
                        const column = _.find(data.Columns, (col) => col.Key == d.key);
                        return {
                            type: SettingsType.ItemsMgmtDialog,
                            label: column.JsonName,
                            dataPath: "EnumSettings[" + i + "].data",
                            dialogStaticSize: { w: 0.5, h: 0.8 },
                            itemsInfo: [
                                {
                                    label: "Label",
                                    type: SettingsType.String,
                                    dataPath: "label",
                                },
                                {
                                    label: "Value",
                                    type: SettingsType.String,
                                    dataPath: "value",
                                }
                            ],
                            createNewItem: () => {
                                let key = getRandomSmallKey();
                                return {
                                    key: key,
                                    value: "value-" + key,
                                    label: "option-" + key
                                }
                            },
                            toolbarToggle: [
                                {
                                    type: SettingsType.Boolean,
                                    label: "Multiple",
                                    dataPath: "EnumSettings[" + i + "].multi",
                                },
                                {
                                    type: SettingsType.Boolean,
                                    label: "Creatable",
                                    dataPath: "EnumSettings[" + i + "].creatable",
                                }
                            ]
                        } as SettingsElementAll<any>;
                    })
                    .value()
            },
            //-------------------------------------------------------------+++ DropdownMenu
            {
                name: "Dropdown Menu Settings",
                elementsFunc: (data) => _(data.DropdownMenuSettings)
                    .filter((d: any) => {
                        const column = _.find(data.Columns, (col) => col.Key == d.key);
                        return isDefined(column);
                    })
                    .map((d, i) => {
                        const column = _.find(data.Columns, (col) => col.Key == d.key);

                        return {
                            type: SettingsType.ItemsMgmtDialog,
                            label: column.Title,
                            dataPath: "DropdownMenuSettings[" + i + "].dropdownMenuItems",
                            dialogStaticSize: { w: 0.5, h: 0.8 },
                            itemsInfo: ToolbarIconInfo_GetSettingList(false, true),
                            createNewItem: () => {
                                let key = getRandomSmallKey();
                                return {
                                    key: key,
                                    value: "value-" + key,
                                    tooltip: "Tooltip",
                                    label: "option-" + key,
                                    id: "Icon " + getRandomSmallKey(),
                                    type: 'icon',
                                    iconColor: 'black',
                                    iconType: eIconType.fontAwesome
                                }
                            }

                        } as SettingsElementAll<any>;
                    })
                    .value()
            },
            /**
             *  
                            {
                                type: SettingsType.ItemsMgmtDialog,
                                label: "Dropdown Items",
                                tooltip: "Extra custom icons in toolbar",
                                dataPath: "dropdownItems",
                                icon: <EditIcon />,
                                itemsInfo: ToolbarIconInfo_GetSettingList(false, true), //todo probably pass a flag when is a table
                                createNewItem: () => {
                                    return {
                                        id: "Icon " + getRandomSmallKey(),
                                        icon: "",
                                        tooltip: "Icon tooltip",
                                        iconColor: "gray",
                                        iconType: eIconType.fontAwesome,
                                        bubbleBackgroundColor: '#D0021B'
                                    } as ToolbarIconInfo;
                                }
                            },
             */
            // ------------------------------------------------------------  +++ Progress
            {
                name: "Progress Settings",
                elementsFunc: (data) => _(data.Columns)
                    .filter((d: ColumnSettings) => d.Type == ColumnType.progress)
                    .map((d, i) => {
                        let index = _.findIndex(data.Columns, x => x.Key == d.Key);
                        return {
                            type: SettingsType.Group,
                            name: d.Title + "[" + d.JsonName + "]",
                            elements: [
                                {
                                    type: SettingsType.Boolean,
                                    label: "Circle format",
                                    dataPath: "Columns[" + index + "].progressIsCircle",
                                },
                                {
                                    type: SettingsType.ColorPicker,
                                    label: "Color",
                                    dataPath: "Columns[" + index + "].progressColor"
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: "Width",
                                    dataPath: "Columns[" + index + "].progressWidth",
                                    maxValue: 20,
                                    minValue: 1,
                                    stepValue: 1,
                                    valuePosfix: "px"
                                },
                                {
                                    type: SettingsType.Boolean,
                                    label: "Show Value",
                                    dataPath: "Columns[" + index + "].progressShowValue",
                                },
                                {
                                    type: SettingsType.ColorPicker,
                                    label: "Circle Trail Color",
                                    dataPath: "Columns[" + index + "].progressTrailColor",
                                    isHidden: (data) => !data.Columns[index].progressIsCircle
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: "Circle Trail Width",
                                    dataPath: "Columns[" + index + "].progressTrailWidth",
                                    maxValue: 10,
                                    minValue: 1,
                                    stepValue: 1,
                                    valuePosfix: "px",
                                    isHidden: (data) => !data.Columns[index].progressIsCircle
                                },
                            ]
                        } as SettingsElementAll<any>;
                    })
                    .value()
            },
            // ------------------------------------------------------------  +++ Image
            {
                name: "Image Settings",
                elementsFunc: (data) => {
                    // try get 1 image element
                    let imgElement: ColumnSettings = null;

                    for (let i = 0; i < data.Columns.length; i++) {
                        const element = data.Columns[i];
                        if (element.Type == ColumnType.img) {
                            imgElement = element;
                            break;
                        }
                    }
                    // check image element was found
                    if (!isDefined(imgElement))
                        return [];

                    return [
                        {
                            type: SettingsType.ImageFileInput,
                            label: "Default Image",
                            tooltip: 'Image to draw when json has no value.',
                            dataPath: "DefaultImageURL",
                            externalURL: true,
                            previewImg: true,
                            fileInterceptor: (value: string) => {
                                return new Promise<string>((resolve, reject) => {
                                    DomUtils.getResizedImage(value, 128, 128, "png")
                                        .then(res => {
                                            resolve(res.src);
                                        })
                                        // handle error
                                        .catch(er => {
                                            reject(er);
                                        });
                                });
                            }
                        },
                        {
                            type: SettingsType.Slider,
                            label: 'Width',
                            tooltip: "Define image's width.",
                            dataPath: "ImgWidth",
                            maxValue: 512,
                            minValue: 24,
                            stepValue: 1,
                            valuePosfix: "px"
                        },
                        {
                            type: SettingsType.Slider,
                            label: 'Height',
                            tooltip: "Define image's height.",
                            dataPath: "ImgHeight",
                            maxValue: 512,
                            minValue: 24,
                            stepValue: 1,
                            valuePosfix: "px"
                        },
                        {
                            type: SettingsType.DropDown,
                            label: 'Resize policy',
                            tooltip: 'Define the image resize policy. Available options: Fill, Contain, Cover, Scale-down, None',
                            dataPath: "ObjectFit",
                            items: [
                                { value: "fill", label: "Fill" },
                                { value: "contain", label: "Contain" },
                                { value: "cover", label: "Cover" },
                                { value: "scale-down", label: "Scale-down" },
                                { value: "none", label: "None" }
                            ]
                        }
                    ];
                }
            },
            // --------------------------------------------------------------
            {
                name: "Formats",
                elements: [
                    {
                        type: SettingsType.DropDown,
                        label: 'Currency',
                        tooltip: "Specify the symbol to use at Currency fields. Available options: USD, GBP and Euro",
                        dataPath: "PriceCurrency",
                        items: [
                            { value: Currency.USD, label: "USD" },
                            { value: Currency.GBP, label: "GBP" },
                            { value: Currency.Euro, label: "Euro" },
                            { value: Currency.ZAR, label: "ZAR" }
                        ]
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Date',
                        tooltip: "Specify the date format. Available options: Short, Long, ISO and ISO ms",
                        dataPath: "DateFormat",
                        items: [
                            { value: DateFormat.Short, label: "Short" },
                            { value: DateFormat.Long, label: "Long" },
                            { value: DateFormat.ISO, label: "ISO" },
                            { value: DateFormat.ISOMS, label: "ISO ms" },
                        ]
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Time',
                        tooltip: "Specify the time format. Available options: Short, Long and Long ms.",
                        dataPath: "timeFormat",
                        items: [
                            { value: TimeFormat.short, label: "Short" },
                            { value: TimeFormat.long, label: "Long" },
                            { value: TimeFormat.longMs, label: "Long ms" }
                        ]
                    },
                ]
            },
            {
                name: "Edit Settings",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Add Row',
                        tooltip: 'If enabled, the toolbar will provide a button for adding a new row.',
                        dataPath: "ShowAddRow",
                        isHidden: (data) => !data.Editable
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Add Duplicate Row Policy',
                        tooltip: 'How to solve the violation of primary key constraint',
                        dataPath: 'AddDuplicateRowPolicy',
                        inSeperateLine: true,
                        isHidden: (data) => !data.ShowAddRow,
                        items: [
                            {
                                label: 'Allow',
                                value: AddDuplicateRowPolicy.Allow
                            },
                            {
                                label: 'Prevent',
                                value: AddDuplicateRowPolicy.Prevent
                            },
                            {
                                label: 'Update',
                                value: AddDuplicateRowPolicy.Update
                            },
                            {
                                label: 'Ask',
                                value: AddDuplicateRowPolicy.Ask
                            }
                        ]
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Edit Row',
                        tooltip: "If enabled, an extra column will be added to table for row editing.",
                        dataPath: "ShowEditRow",
                        isHidden: (data) => !data.Editable
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "On Save Send All",
                        tooltip: "On save request don't send only the changes rows but send the updated table.",
                        dataPath: "OnSaveSendEverything",
                        isHidden: (data) => !data.Editable
                    }
                ]
            },
            {
                name: "Excel Download",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Excel Download',
                        tooltip: 'If enabled, the toolbar with provide an option for exporting data into .xlsx or .csv file.',
                        dataPath: 'DownloadExcel'
                    },
                    {
                        type: SettingsType.String,
                        label: "File Name",
                        dataPath: "DownloadExcelOptions.fileName",
                        tooltip: 'Define excel file name',
                        oneLine: false,
                        isHidden: (data) => !data.DownloadExcel
                    }
                ]
            },
            {
                name: "CSV Import",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'CSV Import',
                        tooltip: 'If enabled, the toolbar will provide an option for importing items from a  CSV file, and then map the CSV column data to table column elements.',
                        dataPath: 'CSVImport'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'First row is headers',
                        dataPath: 'CSVImportSettings.FirstRowIsHeaders',
                        tooltip: 'If true, the first row of parsed data will be interpreted as field names'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Trim column data',
                        dataPath: 'CSVImportSettings.TrimColumnData',
                        tooltip: 'If true, removes leading and trailing whitespace or specified characters from strings'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Dynamic typing',
                        dataPath: 'CSVImportSettings.DynamicTyping',
                        tooltip: 'If true, numeric and boolean data will be converted to their type instead of remaining strings'
                    }
                ]
            },
            {
                name: "Others",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Enable Text Wrapping',
                        tooltip: 'Wraps text onto the next line (by default)',
                        dataPath: 'WrapEnableByDefault'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Enable Filtering',
                        tooltip: 'Appears the filtering toolbar (by default)',
                        dataPath: 'FilterEnableByDefault',
                        isHidden: d => !d.filterable
                    }
                ]
            },
            {
                name: "Toolbar",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Toolbar Icons",
                        tooltip: "Extra custom icons in toolbar",
                        dataPath: "ToolbarIcons",
                        itemsInfo: ToolbarIconInfo_GetSettingList(),
                        createNewItem: () => {
                            return {
                                icon: "",
                                tooltip: "Icon tooltip",
                                iconType: eIconType.fontAwesome,
                                id: "Icon " + getRandomSmallKey(),
                                iconColor: "#2c2c2c",
                                nestedItems: []
                            } as ToolbarIconInfo;
                        }
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Height",
                        dataPath: "ToolbarHeight",
                        minValue: 20,
                        maxValue: 80,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Icon Size",
                        dataPath: "ToolbarIconSize",
                        minValue: 14,
                        maxValue: 50,
                        stepValue: 1,
                        valuePosfix: "px",
                    }
                ]
            },
            {
                name: "Paging",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Paging Support',
                        tooltip: 'Splits the rows into individual pages',
                        dataPath: "PagingSupport"
                    },
                    {
                        type: SettingsType.Dialog,
                        label: 'Page Lengths',
                        dataPath: "PageListSettings",
                        icon: <i className="fas fa-pen" style={{ fontSize: 16 }} />,
                        dialogConf: {
                            contentStaticSize: { w: 0.5, h: 0.5 },
                            initPrivData: (data) => {
                                if (!isDefined(data))
                                    return getDefaultPageListSettings();
                                else
                                    return _.cloneDeep(data);
                            },
                            title: "Page Lengths",
                            bodyFunc: (w, h, di) => {
                                return <TableMUI.Table selectable={false} multiSelectable={false} style={{ width: w }} fixedHeader={true}>
                                    <TableMUI.TableHeader displaySelectAll={false} adjustForCheckbox={false} enableSelectAll={false} >
                                        <TableMUI.TableRow>
                                            <TableMUI.TableHeaderColumn colSpan={2} key="Value-Header" style={{ textAlign: "center" }}>
                                                Value
                                            </TableMUI.TableHeaderColumn>
                                            <TableMUI.TableHeaderColumn colSpan={1} key="Default-Header" style={{ textAlign: "center" }}>
                                                Default
                                            </TableMUI.TableHeaderColumn>
                                            <TableMUI.TableHeaderColumn colSpan={1} key="Delete-Header" style={{ textAlign: "center" }}>
                                            </TableMUI.TableHeaderColumn>
                                        </TableMUI.TableRow>
                                    </TableMUI.TableHeader>

                                    <TableMUI.TableBody
                                        displayRowCheckbox={false}
                                        stripedRows={true}>
                                        {
                                            di.privData.PageList.map((element, index) => {
                                                return (
                                                    <TableMUI.TableRow key={index}>
                                                        <TableMUI.TableRowColumn colSpan={2} style={{ textAlign: "center" }} >
                                                            <TextField
                                                                key={"Settings-Value-" + index}
                                                                id={"Settings-Value-" + index}
                                                                value={element}
                                                                type={"number"}
                                                                onChange={(event) => {
                                                                    let value = parseInt((event.target as any).value, 10);
                                                                    di.privData.PageList[index] = (value > 0) ? value : 0;
                                                                    di.updatePrivData(di.privData);
                                                                }}
                                                                fullWidth={true}
                                                                errorText={(di.privData.PageList.indexOf(element) != di.privData.PageList.lastIndexOf(element)) ? " " : ""}
                                                            />
                                                        </TableMUI.TableRowColumn>

                                                        <TableMUI.TableRowColumn colSpan={1} style={{ textAlign: "center" }} >
                                                            <Checkbox
                                                                style={{ width: 0, margin: "0 auto" }}
                                                                checkedIcon={<RadioOn />}
                                                                uncheckedIcon={<RadioOff />}
                                                                checked={element == di.privData.DefaultSelected}
                                                                onCheck={(event, isInputChecked) => {
                                                                    di.privData.DefaultSelected = element;
                                                                    di.updatePrivData(di.privData);
                                                                }}
                                                            />
                                                        </TableMUI.TableRowColumn>

                                                        <TableMUI.TableRowColumn colSpan={1} style={{ textAlign: "center" }} >
                                                            <IconButton key={"Settings-Columns-Delete-" + index}
                                                                style={{ width: 0, margin: "0 auto" }}
                                                                touch={true}
                                                                onClick={() => {
                                                                    if (element == di.privData.DefaultSelected) {
                                                                        di.privData.DefaultSelected = -1;
                                                                    }
                                                                    di.privData.PageList.splice(index, 1);
                                                                    di.updatePrivData(di.privData);
                                                                }}><DeleteIcon /></IconButton>
                                                        </TableMUI.TableRowColumn>
                                                    </TableMUI.TableRow>
                                                );
                                            })
                                        }
                                    </TableMUI.TableBody>
                                </TableMUI.Table>
                            },

                            actionsFunc: (di) => [
                                <IconButton
                                    key={'settings-add-new-length-icon'}
                                    touch={true}
                                    tooltip={'Add new Length'}
                                    tooltipPosition={'bottom-left'}
                                    onClick={() => {
                                        di.privData.PageList.push(0);
                                        di.updatePrivData(di.privData);
                                    }}
                                >
                                    <AddIcon color="white" />
                                </IconButton>,
                                <FlatButton
                                    label='Cancel'
                                    secondary={true}
                                    onClick={() => {
                                        di.closeDialogCB();
                                    }}
                                />,
                                <FlatButton
                                    label='Save'
                                    primary={true}
                                    onClick={() => {
                                        let updatedData = di.privData;
                                        updatedData.PageList = _.sortBy(updatedData.PageList);
                                        di.updateData(updatedData);
                                        di.closeDialogCB();
                                    }}
                                />
                            ]

                        } as SettingsDialogConf<{ PageList: number[], DefaultSelected: number }, { PageList: number[], DefaultSelected: number }>,

                    }
                ]
            },
            {
                name: "Theming",
                elements: [
                    {
                        type: SettingsType.ColorPicker,
                        dataPath: "Theme.toolbarIconColor",
                        label: "Toolbar Icon"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        dataPath: "Theme.rowSelectedBackgroundColor",
                        label: "Selected Row"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        dataPath: "Theme.rowEditedBackgroundColor",
                        label: "Edited Row"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        dataPath: "Theme.rowAddedBackgroundColor",
                        label: "Added Row"
                    },
                    {
                        type: SettingsType.DropDown,
                        dataPath: 'Theme.textAlign',
                        label: 'Text Align',
                        tooltip: 'Specifies the horizontal alignment of text (rows & header) in the table',
                        items: [
                            { label: 'Left', value: ReactTableTextAlign.Left },
                            { label: 'Center', value: ReactTableTextAlign.Center },
                            { label: 'Right', value: ReactTableTextAlign.Right }
                        ]
                    },
                    {
                        type: SettingsType.Group,
                        name: "Row",
                        elements: [
                            {
                                type: SettingsType.ColorPicker,
                                dataPath: "Theme.rowFontColor",
                                label: "Text"
                            },
                            {
                                type: SettingsType.DropDown,
                                dataPath: "Theme.rowLineBottomStyle",
                                label: "Bottom Style",
                                items: [
                                    { label: "Solid", value: "solid" },
                                    { label: "Dotted", value: "dotted" },
                                    { label: "Dashed", value: "dashed" },
                                    { label: "Double", value: "double" },
                                    { label: "Groove", value: "groove" },
                                    { label: "Hidden", value: "hidden" },
                                ]
                            },
                            {
                                type: SettingsType.ColorPicker,
                                dataPath: "Theme.rowLineBottomColor",
                                label: "Bottom Line"
                            },
                            {
                                type: SettingsType.Slider,
                                dataPath: "Theme.rowLineBottomWidth",
                                label: "Bottom Thickness",
                                maxValue: 20,
                                minValue: 1,
                                stepValue: 1,
                                valuePosfix: "px"
                            }
                        ]
                    },
                    {
                        type: SettingsType.Group,
                        name: "Header",
                        elements: [
                            {
                                type: SettingsType.ColorPicker,
                                dataPath: "Theme.headFontColor",
                                label: "Text"
                            },
                            {
                                type: SettingsType.DropDown,
                                dataPath: "Theme.headLineBottomStyle",
                                label: "Bottom Style",
                                items: [
                                    { label: "Solid", value: "solid" },
                                    { label: "Dotted", value: "dotted" },
                                    { label: "Dashed", value: "dashed" },
                                    { label: "Double", value: "double" },
                                    { label: "Groove", value: "groove" },
                                    { label: "Hidden", value: "hidden" },
                                ]
                            },
                            {
                                type: SettingsType.ColorPicker,
                                dataPath: "Theme.headLineBottomColor",
                                label: "Bottom Line"
                            },
                            {
                                type: SettingsType.Slider,
                                dataPath: "Theme.headLineBottomWidth",
                                label: "Bottom Thickness",
                                maxValue: 20,
                                minValue: 1,
                                stepValue: 1,
                                valuePosfix: "px"
                            }
                        ]
                    },
                    {
                        type: SettingsType.Group,
                        name: 'Filters',
                        elements: [{
                            type: SettingsType.Group,
                            name: 'Select',
                            elements: [{
                                type: SettingsType.ColorPicker,
                                dataPath: 'Theme.selectFontColor',
                                label: 'Font Color',
                                tooltip: 'Sets the color of the text inside the select',
                            },
                            {
                                type: SettingsType.ColorPicker,
                                dataPath: 'Theme.selectBackgroundColor',
                                label: 'Background Color',
                                tooltip: 'Sets the background color of the select'
                            },
                            {
                                type: SettingsType.ColorPicker,
                                dataPath: 'Theme.selectBackgroundColorFocused',
                                label: 'Focused Background Color',
                                tooltip: 'Sets the background color of the focused menu option'
                            },
                            {
                                type: SettingsType.ColorPicker,
                                dataPath: 'Theme.selectBackgroundColorSelected',
                                label: 'Selected Background Color',
                                tooltip: 'Sets the background color of the selected menu option'
                            }]
                        }]
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                this.setState({ WidgetData: data },
                    () => {
                        if (isDefined(this.props.onWidgetDataUpdate))
                            this.props.onWidgetDataUpdate(this.state.WidgetData);
                    }
                )
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
// Table Description interface
// ------------------------------------------------------------------------------------------------
enum LocalizationLabelKey {
    column = 'column_',
    tooltip = 'tooltip_',
    editRowButton = 'button_edit_',
    enumValue = 'enum_'
}
// ------------------------------------------------------------------------------------------------
export class TableWidget extends IWidgetThingsClass<TableData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <Table />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <TableWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        let savedSettings: TableSettings = _.cloneDeep(this.WidgetData);
        if (savedSettings != null) {
            // Clean old ports
            thing.Ports = {};

            // Add the ports
            this._GetPorts(savedSettings).forEach((port) => {
                thing.AddPort(port, false);
            });
        }

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = this._GetPorts(wData);

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (this.WidgetData.Key == key || TableKeys.InsertOrUpdatePortKey == key)
            return tableDataJsonSchemaValidator().DataValidateString(value);
        else if (key == TableKeys.ServerData)
            return serverDataValidator().DataValidateString(value);
        else if (key == TableKeys.Settings)
            return settingsValidator().DataValidateString(value);
        else if (key == TableKeys.CustomColumns)
            return customColumnsValidator().DataValidateString(value);
        else
            return EnumTypesJsonSchemeValidator().DataValidateString(value);
    }
    // ------------------------------------------------------------------------------------------------
    private _GetPorts(tableSettings: TableSettings): WebThingPort[] {
        let ports: WebThingPort[] = [];

        if (tableSettings != null) {
            ports.push(new WebThingPort(
                {
                    Name: "Data",
                    PortKey: tableSettings.Key,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "",
                    State: ""
                },
                WebThingPortType.Data,
                (data) => this.ValidateValues(tableSettings.Key, data)
            ));

            ports.push(new WebThingPort(
                {
                    Name: 'Insert or Update',
                    PortKey: TableKeys.InsertOrUpdatePortKey,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "Update existing data or insert new one.",
                    State: ""
                },
                WebThingPortType.Data,
                (data) => this.ValidateValues(TableKeys.InsertOrUpdatePortKey, data)));

            ports.push(new WebThingPort(
                {
                    Name: 'Settings',
                    PortKey: TableKeys.Settings,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'Override widget configuration',
                    State: ''
                },
                WebThingPortType.Data,
                data => this.ValidateValues(TableKeys.Settings, data))
            );

            ports.push(new WebThingPort(
                {
                    Name: 'Custom Columns',
                    PortKey: TableKeys.CustomColumns,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'Create custom columns',
                    State: ''
                },
                WebThingPortType.Data,
                data => this.ValidateValues(TableKeys.CustomColumns, data))
            );

            for (let i = 0; i < tableSettings.Columns.length; i++) {
                const column = tableSettings.Columns[i];
                // check if button column
                if (column.Type == ColumnType.button) {
                    ports.push(new WebThingPort(
                        {
                            Name: column.Title,
                            PortKey: column.Key,
                            Type: Plegma.ePortType.JsonString,
                            ioDirection: Plegma.ioPortDirection.Output,
                            ConfFlags: Plegma.ePortConf.None,
                            Description: "",
                            State: ""
                        },
                        WebThingPortType.Trigger)
                    );
                }
                // check if enum column
                else if (column.Type == ColumnType.enum) {

                    // try find enum
                    const enumEntry = _.find(tableSettings.EnumSettings, (e) => e.key == column.Key);
                    if (!isDefined(enumEntry)) {
                        DebugEx.TraceCritical("Should not be here");
                        continue;
                    }

                    ports.push(new WebThingPort(
                        {
                            Name: column.Title,
                            PortKey: column.Key,
                            Type: Plegma.ePortType.JsonString,
                            ioDirection: Plegma.ioPortDirection.Input,
                            ConfFlags: Plegma.ePortConf.None,
                            Description: "Set custom key/values to enum column",
                            State: ""
                        },
                        WebThingPortType.Data,
                        (data) => this.ValidateValues(column.Key, data))
                    );
                }
            }

            if ((tableSettings.Columns).findIndex(el => el.Type === 13) !== -1) {
                ports.push(new WebThingPort(
                    {
                        Name: 'onDropdownMenuItemClick',
                        PortKey: TableKeys.onDropdownMenuItemClick,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Output,
                        ConfFlags: Plegma.ePortConf.None,
                        Description: "",
                        State: ""
                    },
                    WebThingPortType.Data)
                );
            }

            // different port for server data
            if (tableSettings.ServerSideProcessing) {
                ports.push(new WebThingPort({
                    Name: 'Server Data',
                    PortKey: TableKeys.ServerData,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'Contains the rows of the current page, and optionally the total number of pages',
                    State: ''
                }, WebThingPortType.Data));

                ports.push(new WebThingPort({
                    Name: 'Fetch Data',
                    PortKey: TableKeys.FetchData,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'Requests new data when things (pagination, sorting, filtering) change',
                    State: ''
                }, WebThingPortType.Data));
            }

            ports.push(new WebThingPort({
                Name: 'Selected data',
                PortKey: TableKeys.SelectDataPortKey,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: tableSettings.Selectable ? Plegma.ePortConf.None : Plegma.ePortConf.Hidden,
                Description: "",
                State: ""
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'Edit data',
                PortKey: TableKeys.EditDataPortKey,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: tableSettings.Editable ? Plegma.ePortConf.None : Plegma.ePortConf.Hidden,
                Description: "",
                State: ""
            }, WebThingPortType.Trigger));

            ports.push(new WebThingPort({
                Name: 'Add data',
                PortKey: TableKeys.AddDataPortKey,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: tableSettings.Editable ? Plegma.ePortConf.None : Plegma.ePortConf.Hidden,
                Description: "",
                State: ""
            }, WebThingPortType.Trigger));

            ports.push(new WebThingPort({
                Name: 'onCellClick',
                PortKey: TableKeys.onCellClick,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'onPageChange',
                PortKey: TableKeys.onPageChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'onToolbarIconClick',
                PortKey: TableKeys.onToolbarIconClick,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'onRowUpdate',
                PortKey: TableKeys.onRowUpdate,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'Outputs the index and the values of the updated row',
                State: ''
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'onVisibilityChange',
                PortKey: TableKeys.onVisibilityChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            }, WebThingPortType.Data));
        }
        return ports;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): TableData {
        return _.cloneDeep(DefaultTableSettings);
    }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: TableData): TableData {
        // If we have old settings pass them
        if (isDefined(data.TableSettings)) {
            _.defaults(data, data.TableSettings, DefaultTableSettings);
            delete data.TableSettings;
        } else {
            // If we missing table settings then set the default values
            _.defaults(data, DefaultTableSettings);
        }

        // fix empty `TableSettings.Key`
        if (isStringEmpty(data.Key)) {
            data.Key = DefaultTableSettings.Key;
        }

        // Set the default values in each column
        const newWidth: number = 100 / data.Columns.length;
        data.Columns.forEach(c => {
            _.defaults(c, { Width: newWidth }, DefaultColumnSettings);

            if (c.Type == ColumnType.color) {
                c.Sortable = false;
                c.Filtering = false;
            }
        })
        return data;
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() {
        return TableWidget.GetDefaultWidgetDataStatic();
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        var data = TableWidget.GetDefaultWidgetDataStatic();

        // fill sample columns
        data.Columns = [
            {
                Key: getRandomSmallKey(), JsonName: "col1", JsonNameUniqueError: false, Title: "Column 1",
                Type: ColumnType.text, IsPrimaryKey: false, Sortable: false, Filtering: false, Hidden: false,
                NonEditable: false, AutoHiddable: false, Tooltip: "", Icon: IconType.add, Width: 50, PivotBy: false, IsMandatory: false,
                CheckedIcon: "fa-check-square-o",
                UncheckedIcon: "fa-square-o",
                BackgroundColorAccessor: null,
                FontColorAccessor: null,
                StyleAccessor: null,
                VisibleInAdd: true
            },
            {
                Key: getRandomSmallKey(), JsonName: "col2", JsonNameUniqueError: false, Title: "Column 2",
                Type: ColumnType.text, IsPrimaryKey: false, Sortable: true, Filtering: false, Hidden: false, IsMandatory: false,
                NonEditable: false, AutoHiddable: false, Tooltip: "", Icon: IconType.add, Width: 50, PivotBy: false,
                CheckedIcon: "fa-check-square-o",
                UncheckedIcon: "fa-square-o",
                BackgroundColorAccessor: null,
                FontColorAccessor: null,
                StyleAccessor: null,
                VisibleInAdd: true
            }
        ];

        // fill sample `PageListSettings`
        data.PageListSettings = getDefaultPageListSettings();

        return {
            Title: "Table",
            WidgetTitle: "",
            Key: "TableWidget",
            Type: "TableWidget",
            Data: data,
            Description: "Table widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: IconURIPath + "/svg/table.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    GetLocalizationDictionary(): LocalizationMap {
        let dict = {};

        // add widget's title (if any)
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            dict[WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        _.forEach(this.WidgetData.Columns, (v: ColumnSettings) => {
            // Add column's name to dictionary.
            dict[LocalizationLabelKey.column + v.Key] = v.Title;

            // For buttons, add also the tooltip.
            if (v.Type == ColumnType.button && !isStringEmpty(v.Tooltip))
                dict[LocalizationLabelKey.tooltip + v.Key] = v.Tooltip;

            // for enums, expose enum values
            if (v.Type == ColumnType.enum) {
                const enumEntry = _.find(this.WidgetData.EnumSettings, (e) => e.key == v.Key);
                if (!isDefined(enumEntry)) {
                    DebugEx.TraceCritical("Should not be here");
                    return;
                }
                _.forEach(enumEntry.data, (el) => {
                    if (!isStringEmpty(el.label))
                        dict[LocalizationLabelKey.enumValue + enumEntry.key + '_' + el.key] = el.label;
                })
            }
        });

        //Expose tooltips for custom icons
        _.forEach(this.WidgetData.ToolbarIcons, (v: ToolbarIconInfo) => {
            dict[LocalizationLabelKey.tooltip + "toolbar-icon" + v.id] = v.tooltip;
        });

        // Expose tooltips for fixed toolbar buttons
        _.forEach(ReactTableStatics.GetLocalizationDictionary(), (value, key) => {
            dict[key] = value;
        })

        if (this.WidgetData.ShowEditRow)
            dict[LocalizationLabelKey.editRowButton] = editRowTooltip;

        return dict;
    };
    // ------------------------------------------------------------------------------------------------
}
const editRowTooltip = "Edit row";
// ------------------------------------------------------------------------------------------------
const EnumTypesJsonSchemeValidator = () => JsonSchemaValidator.GetInstance("EnumTypesJsonSchemeValidator", {
    title: "EnumTypes",
    type: "array",
    items: {
        type: "object",
        properties: {
            label: { type: "string" },
            value: { type: ["string", "number"] },
        },
        required: ["label", "value"]
    },
    minItems: 0
});
// ------------------------------------------------------------------------------------------------
const tableDataJsonSchemaValidator = () => JsonSchemaValidator.GetInstance("TableSchemeValidator", {
    type: "array",
    items: {
        type: "object"
    },
    minItems: 0
});
// ------------------------------------------------------------------------------------------------
const customColumnsValidator = () => JsonSchemaValidator.GetInstance('CustomColumnsPortValidator', {
    type: 'array',
    items: {
        type: 'object',
        required: ['Key', 'JsonName'],
        properties: {
            Key: { type: 'string' },
            Title: { type: 'string' },
            Width: { type: 'number' },
            Hidden: { type: 'boolean' },
            Tooltip: { type: 'string' },
            JsonName: { type: 'string' },
            PivotBy: { type: 'boolean' },
            Sortable: { type: 'boolean' },
            Filtering: { type: 'boolean' },
            CheckedIcon: { type: 'string' },
            NonEditable: { type: 'boolean' },
            AutoHiddable: { type: 'boolean' },
            UncheckedIcon: { type: 'string' },
            IsPrimaryKey: { type: 'boolean' },
            BackgroundColorAccessor: { type: 'string' },
            FontColorAccessor: { type: 'string' },
            StyleAccessor: { type: 'string' },
            VisibleInAdd: { type: 'boolean' },
            Icon: {
                type: 'number',
                enum: [0, 1, 2, 3, 4]
            },
            Type: {
                type: 'number',
                enum: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 13]
            },
            EnumSettings: {
                type: 'object',
                required: ['data'],
                properties: {
                    multi: { type: 'boolean' },
                    data: {
                        type: 'array',
                        items: {
                            type: 'object',
                            required: ['label', 'value'],
                            properties: {
                                label: { type: 'string' },
                                value: { type: ['string', 'number'] }
                            }
                        }
                    }
                }
            }
        }
    }
});
// ------------------------------------------------------------------------------------------------
const settingsValidator = () => JsonSchemaValidator.GetInstance('SettingsPortValidator', {
    type: 'object',
    properties: {
        Editable: { type: 'boolean' },
        ShowAddRow: { type: 'boolean' }
    }
});
// ------------------------------------------------------------------------------------------------
const serverDataValidator = () => JsonSchemaValidator.GetInstance('ServerDataPortValidator', {
    type: 'object',
    properties: {
        rows: {
            type: 'array',
            items: { type: 'object' },
            minItems: 0
        },
        pages: { type: 'number' }
    }
});
// ------------------------------------------------------------------------------------------------
